import { useEffect, useState } from "react";
import { WarningCircle } from "@phosphor-icons/react";
import { api, fmtGBP2, fmtDateUK } from "@/lib/api";

const fmtQty = (n) => Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 2 });
const fmtUnit = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Shared by the panel and the AI narrative so the report fetches alerts once.
export function usePriceAlerts(token, dateFrom, dateTo, siteId, enabled = true) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    if (!dateFrom || !dateTo) return;
    let live = true;
    setData(null);
    api.get(`/public/clients/${token}/price-alerts`, { params: { date_from: dateFrom, date_to: dateTo, site_id: siteId || undefined } })
      .then((r) => live && setData(r.data))
      .catch(() => live && setData({ alerts: [], error: true }));
    return () => { live = false; };
  }, [token, dateFrom, dateTo, siteId, enabled]);
  return data;
}

export function PriceAlertsPanel({ data, onSelectInvoice }) {
  const [showAll, setShowAll] = useState(false);

  const alerts = data?.alerts || [];
  const rows = showAll ? alerts : alerts.slice(0, 8);

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg card-fade mb-5" data-testid="price-alerts-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap px-6 pt-5 pb-4 border-b border-[#EEEEEE]">
        <div>
          <p className="uppercase text-[11px] tracking-[0.18em] text-[#B45309] font-medium flex items-center gap-1.5">
            <WarningCircle size={13} weight="fill" /> Item price alerts
          </p>
          <h3 className="font-display text-[18px] font-semibold tracking-tight mt-1">
            {data === null ? "Checking prices…" : alerts.length ? `${alerts.length} item${alerts.length === 1 ? "" : "s"} up more than 5%` : "No price rises over 5%"}
          </h3>
        </div>
        <p className="text-[11px] text-[#94A3B8] max-w-sm leading-[1.5]">
          Xero line Unit Price vs that supplier&rsquo;s median Unit Price for the item over the prior 90 days (2+ purchases). Quantities shown for context; rises over 90% are ignored as likely pack or unit changes.
        </p>
      </div>
      {alerts.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="price-alerts-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-[0.12em] text-[#475569] text-left">
                <th className="px-4 sm:px-6 py-2.5 font-medium">Item</th>
                <th className="px-3 py-2.5 font-medium hidden sm:table-cell">Supplier</th>
                <th className="px-3 py-2.5 font-medium text-right hidden sm:table-cell">Qty usual → latest</th>
                <th className="px-3 py-2.5 font-medium text-right hidden sm:table-cell">Usual unit price</th>
                <th className="px-3 py-2.5 font-medium text-right hidden sm:table-cell">Latest unit price</th>
                <th className="px-3 py-2.5 font-medium text-right">Rise</th>
                <th className="px-3 py-2.5 font-medium text-right max-sm:pr-4">Extra cost</th>
                <th className="px-6 py-2.5 font-medium text-right hidden sm:table-cell">Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a, i) => (
                <tr
                  key={`${a.supplier}-${a.description}`}
                  onClick={() => onSelectInvoice?.(a.invoice_id)}
                  className="border-t border-[#EEEEEE] hover:bg-[#F5F5F5] cursor-pointer transition-colors"
                  data-testid={`price-alert-row-${i}`}
                >
                  <td className="px-4 sm:px-6 py-2.5 text-[#0F172A] font-medium max-w-[150px] sm:max-w-[280px] truncate" title={a.description}>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full mr-2 align-middle ${a.classification === "WET" ? "bg-[#1D4ED8]" : "bg-[#059669]"}`} />
                    {a.description}
                  </td>
                  <td className="px-3 py-2.5 text-[#475569] max-w-[200px] truncate hidden sm:table-cell" title={a.supplier}>{a.supplier}</td>
                  <td className="px-3 py-2.5 text-right font-mono-num text-[#94A3B8] whitespace-nowrap hidden sm:table-cell" data-testid={`price-alert-qty-${i}`}>
                    {fmtQty(a.usual_quantity)} → <span className="text-[#475569]">{fmtQty(a.quantity)}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono-num text-[#475569] hidden sm:table-cell">{fmtUnit(a.avg_price)}</td>
                  <td className="px-3 py-2.5 text-right font-mono-num text-[#0F172A] font-semibold hidden sm:table-cell">{fmtUnit(a.latest_price)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <span className="inline-flex rounded-full px-2 py-0.5 text-[11px] font-mono-num font-semibold bg-rose-50 text-rose-700" data-testid={`price-alert-pct-${i}`}>
                      +{a.pct.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono-num text-[#991B1B] max-sm:pr-4 whitespace-nowrap">{fmtGBP2(a.extra_cost)}</td>
                  <td className="px-6 py-2.5 text-right font-mono-num text-[#94A3B8] hidden sm:table-cell">{fmtDateUK(a.invoice_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {alerts.length > 8 && (
            <button
              onClick={() => setShowAll((v) => !v)}
              className="w-full text-[12px] text-[#475569] hover:text-[#0F172A] py-2.5 border-t border-[#EEEEEE]"
              data-testid="price-alerts-toggle"
            >
              {showAll ? "Show fewer" : `Show all ${alerts.length}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
